import type { UserAccess, UserOverrideInput } from "@nexseed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
  Lock,
  MapPin,
  Minus,
  Pencil,
  Plus,
  SlidersHorizontal,
  User,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { DateInput } from "@/components/app/date-input";
import { SearchField } from "@/components/app/search-field";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  adminApi,
  adminKeys,
  ROLE_DEVICES,
  roleDeviceOf,
} from "@/features/admin/api";
import { ChangesBar } from "@/features/admin/components/changes-bar";
import { PersonSheet } from "@/features/admin/components/person-sheet";
import { Avatar, UserStatusBadge } from "@/features/admin/components/person";
import { ReviewView } from "@/features/admin/components/review-view";
import { MODULE_ICON } from "@/features/admin/module-icons";
import { moduleControls, type ModuleControls } from "@/features/admin/reach";
import type { ReviewLine, ReviewPair } from "@/features/admin/review-model";
import { sectionsOf } from "@/features/admin/role-model";
import { nest } from "@/features/admin/tree";
import { useAuth } from "@/features/auth/store";
import { ApiError } from "@/lib/api-client";
import { cn } from "@/lib/utils";

/**
 * One person's access (samples C2 and C3, 2026-10-05). Access: each module's permissions, locked
 * where a role grants them, and "Add for this person" for a personal grant with a reason and an
 * optional end, without touching the role. Compare: the person line by line against their roles.
 */
export const Route = createFileRoute("/_app/admin/users_/$userId")({
  params: {
    parse: (p) => ({
      userId: z.coerce.number().int().positive().parse(p.userId),
    }),
    stringify: (p) => ({ userId: String(p.userId) }),
  },
  validateSearch: z.object({
    tab: z.enum(["access", "compare"]).default("access"),
  }),
  beforeLoad: () => {
    if (!useAuth.getState().can("users.manage")) throw redirect({ to: "/" });
  },
  component: PersonAccess,
});

/** A personal grant as staged on this screen. */
interface Grant {
  reason: string;
  /** ISO date (YYYY-MM-DD) of the last day it counts, or null for no end. */
  until: string | null;
  /** Ended already (kept so it can be renewed or removed). */
  ended?: boolean;
}

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;
const errText = (e: unknown, fallback: string) =>
  e instanceof ApiError ? e.message : fallback;
const dayOf = (iso: string | null) => (iso ? iso.slice(0, 10) : null);
const fmtDay = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
/** The end of the chosen day in India, the plant's clock. */
const endOfDay = (d: string) => `${d}T23:59:59+05:30`;

const sameGrants = (a: Map<string, Grant>, b: Map<string, Grant>) =>
  a.size === b.size &&
  [...a].every(([k, g]) => {
    const o = b.get(k);
    return !!o && o.reason.trim() === g.reason.trim() && o.until === g.until;
  });

function PersonAccess() {
  const { userId } = Route.useParams();
  const { tab } = Route.useSearch();
  const nav = useNavigate();
  const qc = useQueryClient();
  const selfId = useAuth((a) => a.me?.id ?? null);
  const isSelf = selfId === userId;

  const access = useQuery({
    queryKey: adminKeys.access(userId),
    queryFn: () => adminApi.userAccess(userId),
  });
  const tree = useQuery({
    queryKey: adminKeys.tree,
    queryFn: adminApi.tree,
    retry: false,
  });
  const roles = useQuery({
    queryKey: adminKeys.roles,
    queryFn: adminApi.roles,
  });
  const modules = useMemo(
    () =>
      moduleControls(
        sectionsOf(nest((tree.data?.items ?? []).filter((n) => n.isEnabled)))
      ),
    [tree.data]
  );
  const labelOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of tree.data?.items ?? [])
      if (n.permission) m.set(n.permission, n.label);
    return m;
  }, [tree.data]);

  const base = useMemo(() => {
    const m = new Map<string, Grant>();
    for (const o of access.data?.overrides ?? [])
      m.set(o.permission, {
        reason: o.reason,
        until: dayOf(o.expiresAt),
        ended: !o.live,
      });
    return m;
  }, [access.data]);
  const [staged, setStaged] = useState<Map<string, Grant> | null>(null);
  const grants = staged ?? base;
  const dirty = staged !== null && !sameGrants(staged, base);
  const [review, setReview] = useState(false);
  const [editing, setEditing] = useState(false);

  useBlocker({
    shouldBlockFn: () =>
      dirty && !window.confirm("Leave without saving the personal grants?"),
    enableBeforeUnload: () => dirty,
  });

  const setGrant = (code: string, g: Grant | null) =>
    setStaged((prev) => {
      const next = new Map(prev ?? base);
      if (g) next.set(code, g);
      else next.delete(code);
      return next;
    });

  const save = useMutation({
    mutationFn: () =>
      adminApi.setUserOverrides(
        userId,
        [...grants].map(([permission, g]): UserOverrideInput => ({
          permission,
          reason: g.reason.trim(),
          expiresAt: g.until ? endOfDay(g.until) : null,
        }))
      ),
    onSuccess: async () => {
      toast.success("Saved. They get the new access on their next request.");
      setReview(false);
      await qc.invalidateQueries({ queryKey: ["admin"] });
      setStaged(null);
    },
  });

  const a = access.data;
  if (access.error)
    return (
      <Callout tone="danger" title="This person could not be loaded">
        {errText(access.error, "Try again in a moment.")}
      </Callout>
    );
  if (!a || tree.isLoading)
    return (
      <div className="space-y-3">
        <Skeleton className="h-6 w-60" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    );

  const fromRole = (code: string) =>
    a.permissions.find((p) => p.code === code)?.from.roles ?? [];
  const roleName = (id: number) =>
    a.roles.find((r) => r.id === id)?.name ?? "Role";
  const changes = staged
    ? [...new Set([...staged.keys(), ...base.keys()])].filter((k) => {
        const s = staged.get(k);
        const b = base.get(k);
        return (
          !s || !b || s.reason.trim() !== b.reason.trim() || s.until !== b.until
        );
      }).length
    : 0;

  const pairs: ReviewPair[] = review
    ? modules.flatMap((m) => {
        const lines: ReviewLine[] = m.controls
          .filter((c) => !c.startsWith("form:"))
          .map((c) => {
            const role = fromRole(c).length > 0;
            const before = role || (base.has(c) && !base.get(c)!.ended);
            const after = role || (grants.has(c) && !grants.get(c)!.ended);
            const b = base.get(c);
            const s = grants.get(c);
            const edited =
              !!b &&
              !!s &&
              (b.reason.trim() !== s.reason.trim() || b.until !== s.until);
            return {
              key: c,
              group: role ? "From their roles" : "Personal grants",
              label: labelOf.get(c) ?? c,
              before,
              after,
              tag:
                before && !after
                  ? "removed"
                  : !before && after
                    ? "added"
                    : edited
                      ? "changed"
                      : null,
            };
          });
        const n = lines.filter((l) => l.tag).length;
        return n
          ? [
              {
                key: m.code,
                roleId: userId,
                roleName: a.user.fullName,
                people: 1,
                module: m.code,
                moduleLabel: m.label,
                lines: lines.filter((l) => l.before || l.after || l.tag),
                changes: n,
              },
            ]
          : [];
      })
    : [];

  return (
    <>
      <Band access={a} onEdit={isSelf ? null : () => setEditing(true)} />

      {review ? (
        <ReviewView
          pairs={pairs}
          title="Before and after"
          description={`What changes for ${a.user.fullName} alone. Their roles stay as they are.`}
          publishLabel={`Publish ${plural(changes, "change", "changes")}`}
          publishing={save.isPending}
          error={
            save.error ? errText(save.error, "Could not save the grants") : null
          }
          onBack={() => setReview(false)}
          onPublish={() => save.mutate()}
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <Tabs
              value={tab}
              onValueChange={(v) =>
                void nav({
                  to: ".",
                  search: { tab: v },
                  replace: true,
                })
              }
            >
              <TabsList className="mb-0">
                <TabsTrigger value="access">Access by module</TabsTrigger>
                <TabsTrigger value="compare">Compare with role</TabsTrigger>
              </TabsList>
            </Tabs>
            {isSelf && (
              <span className="text-sm text-ink-muted">
                Your own access is changed by another admin.
              </span>
            )}
          </div>
          {tab === "access" ? (
            <AccessByModule
              access={a}
              modules={modules}
              labelOf={labelOf}
              grants={grants}
              readOnly={isSelf}
              fromRole={fromRole}
              roleName={roleName}
              onGrant={setGrant}
            />
          ) : (
            <CompareWithRole
              access={a}
              modules={modules}
              labelOf={labelOf}
              grants={grants}
              readOnly={isSelf}
              fromRole={fromRole}
              onRemove={(c) => setGrant(c, null)}
            />
          )}
        </>
      )}

      {dirty && !review && (
        <ChangesBar
          count={changes}
          summary={`personal ${changes === 1 ? "grant" : "grants"} for ${a.user.fullName.split(" ")[0]}`}
          onDiscard={() => setStaged(null)}
          onReview={() => {
            save.reset();
            setReview(true);
          }}
        />
      )}

      <PersonSheet
        user={editing ? a.user : null}
        roles={roles.data?.items ?? []}
        isSelf={isSelf}
        matrixPending={false}
        onClose={() => {
          setEditing(false);
          void qc.invalidateQueries({ queryKey: adminKeys.access(userId) });
        }}
      />
    </>
  );
}

// ------------------------------------------------------------------ band

function Band({
  access: a,
  onEdit,
}: {
  access: UserAccess;
  onEdit: (() => void) | null;
}) {
  const clients = [...new Set(a.roles.flatMap((r) => r.clients))];
  const device = a.roles.length
    ? ROLE_DEVICES[roleDeviceOf(clients)].label
    : "—";
  const window =
    a.user.accessActiveFrom || a.user.accessActiveUntil
      ? `${a.user.accessActiveFrom ? fmtDay(a.user.accessActiveFrom.slice(0, 10)) : "Now"} → ${a.user.accessActiveUntil ? fmtDay(a.user.accessActiveUntil.slice(0, 10)) : "no end"}`
      : "Always";
  return (
    <section className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-border bg-surface-raised px-5 py-4 shadow-card">
      <div className="flex min-w-60 items-center gap-3.5">
        <Avatar name={a.user.fullName} seed={a.user.email} size="lg" />
        <div className="min-w-0">
          <div className="truncate text-lg font-extrabold tracking-tight text-ink">
            {a.user.fullName}
          </div>
          <div className="truncate text-sm text-ink-muted">{a.user.email}</div>
        </div>
      </div>
      <div className="hidden w-px self-stretch bg-border md:block" />
      <Field label={a.roles.length === 1 ? "Role" : "Roles"}>
        {a.roles.length ? (
          a.roles.map((r) => (
            <Chip key={r.id} icon={Layers}>
              {r.name}
            </Chip>
          ))
        ) : (
          <span className="text-sm font-semibold text-warning-text">
            No role
          </span>
        )}
      </Field>
      <Field label="Plants">
        {a.user.facilities.length ? (
          a.user.facilities.map((f) => (
            <Chip key={f.id} icon={MapPin}>
              {f.name}
            </Chip>
          ))
        ) : (
          <span className="text-sm font-semibold text-warning-text">
            No plant
          </span>
        )}
      </Field>
      <Field label="Works on">
        <Chip>{device}</Chip>
      </Field>
      <Field label="Access window">
        <Chip icon={Clock}>{window}</Chip>
      </Field>
      <div className="ml-auto flex items-center gap-3">
        <UserStatusBadge user={a.user} />
        {onEdit && (
          <Button variant="secondary" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" /> Edit access
          </Button>
        )}
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({
  icon: Icon,
  children,
}: {
  icon?: typeof Layers;
  children: React.ReactNode;
}) {
  return (
    <span className="inline-flex h-7 items-center gap-1.5 rounded-lg border border-border bg-surface-sunken px-2.5 text-sm font-semibold text-ink-secondary">
      {Icon && <Icon className="h-3.5 w-3.5" aria-hidden />}
      {children}
    </span>
  );
}

// ------------------------------------------------------------------ C2: access by module

interface RowProps {
  access: UserAccess;
  modules: ModuleControls[];
  labelOf: Map<string, string>;
  grants: Map<string, Grant>;
  readOnly: boolean;
  fromRole: (code: string) => number[];
}

function AccessByModule({
  access: a,
  modules,
  labelOf,
  grants,
  readOnly,
  fromRole,
  roleName,
  onGrant,
}: RowProps & {
  roleName: (id: number) => string;
  onGrant: (code: string, g: Grant | null) => void;
}) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const first = name(a.user.fullName);
  const codesOf = (m: ModuleControls) =>
    m.controls.filter(
      (c) =>
        !c.startsWith("form:") &&
        (!needle ||
          `${labelOf.get(c) ?? ""} ${c}`.toLowerCase().includes(needle))
    );
  const [open, setOpen] = useState<Set<string>>(
    () =>
      new Set(
        modules
          .filter((m) => m.controls.some((c) => grants.has(c)))
          .map((m) => m.code)
      )
  );
  const toggle = (code: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <div className="pb-24">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <p className="min-w-60 flex-1 text-sm text-ink-muted">
          A lock means a role gives it. Add something for {first} alone without
          touching the role; each personal grant needs a reason.
        </p>
        <SearchField
          value={q}
          onChange={setQ}
          placeholder="Find a permission"
          className="sm:w-72"
        />
      </div>
      <div className="space-y-2.5">
        {modules.map((m) => {
          const codes = codesOf(m);
          if (!codes.length) return null;
          const role = codes.filter((c) => fromRole(c).length).length;
          const personal = codes.filter(
            (c) => !fromRole(c).length && grants.has(c)
          ).length;
          const Icon = MODULE_ICON[m.code] ?? SlidersHorizontal;
          const isOpen = open.has(m.code) || !!needle;
          return (
            <section
              key={m.code}
              className="overflow-hidden rounded-2xl border border-border bg-surface-raised shadow-card"
            >
              <button
                type="button"
                onClick={() => toggle(m.code)}
                aria-expanded={isOpen}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-3 text-left",
                  isOpen && "border-b border-border"
                )}
              >
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
                    role || personal
                      ? "bg-rail text-brand"
                      : "bg-brand-soft text-brand-text"
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[15px] font-bold text-ink">
                    {m.label}
                  </span>
                  <span className="block text-xs text-ink-muted">
                    {role || personal
                      ? [
                          role ? `${role} of ${codes.length} from role` : null,
                          personal ? `${personal} added for ${first}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")
                      : "Nothing from role"}
                  </span>
                </span>
                {personal > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-manual-border bg-manual-soft px-2 py-0.5 text-xs font-bold text-manual-text">
                    <User className="h-3 w-3" /> {personal}
                  </span>
                )}
                {isOpen ? (
                  <ChevronDown className="h-4 w-4 text-ink-muted" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-ink-muted" />
                )}
              </button>
              {isOpen && (
                <ul>
                  {codes.map((c) => (
                    <GrantRow
                      key={c}
                      code={c}
                      label={labelOf.get(c) ?? c}
                      first={first}
                      roles={fromRole(c).map(roleName)}
                      grant={grants.get(c) ?? null}
                      readOnly={readOnly}
                      onChange={(g) => onGrant(c, g)}
                    />
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

const name = (full: string) => full.split(" ")[0] ?? full;

function GrantRow({
  code,
  label,
  first,
  roles,
  grant,
  readOnly,
  onChange,
}: {
  code: string;
  label: string;
  first: string;
  roles: string[];
  grant: Grant | null;
  readOnly: boolean;
  onChange: (g: Grant | null) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [reason, setReason] = useState("");
  const [until, setUntil] = useState("");
  const byRole = roles.length > 0;
  const state = byRole ? "role" : grant ? "person" : "off";
  const seg =
    "inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-bold transition-colors";
  return (
    <li
      className={cn(
        "border-t border-border-subtle px-4 py-2.5 first:border-t-0",
        state === "person" && "bg-manual-soft/60"
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <span className="font-semibold text-ink">{label}</span>{" "}
          <span className="font-mono text-xs text-ink-muted">{code}</span>
          {byRole && (
            <div className="text-xs text-ink-muted">
              From {roles.join(" and ")}
            </div>
          )}
          {grant && !byRole && (
            <div className="text-xs font-semibold text-manual-text">
              {grant.reason}
              {grant.until && ` · until ${fmtDay(grant.until)}`}
              {grant.ended && " · ended"}
            </div>
          )}
        </div>
        <div
          className="inline-flex gap-0.5 rounded-lg border border-border-strong bg-surface-raised p-0.5"
          role="group"
          aria-label={`${label}: who gives it`}
        >
          <span
            className={cn(
              seg,
              byRole
                ? "bg-neutral-soft text-ink-secondary"
                : "text-ink-muted/50"
            )}
          >
            {byRole && <Lock className="h-3 w-3" />}From role
          </span>
          <button
            type="button"
            disabled={byRole || readOnly || !!grant}
            onClick={() => setAdding(true)}
            className={cn(
              seg,
              state === "person"
                ? "bg-manual-soft text-manual-text"
                : byRole || readOnly
                  ? "text-ink-muted/50"
                  : "text-ink-secondary hover:bg-surface-sunken"
            )}
          >
            {state === "person" ? (
              <>
                <User className="h-3 w-3" /> Added for {first}
              </>
            ) : (
              <>Add for {first}</>
            )}
          </button>
          <button
            type="button"
            disabled={byRole || readOnly}
            onClick={() => {
              setAdding(false);
              onChange(null);
            }}
            className={cn(
              seg,
              state === "off" && !adding
                ? "bg-surface-sunken text-ink-secondary"
                : byRole || readOnly
                  ? "text-ink-muted/50"
                  : "text-ink-secondary hover:bg-surface-sunken"
            )}
          >
            Off
          </button>
        </div>
      </div>
      {adding && !grant && (
        <form
          className="mt-2.5 flex flex-wrap items-end gap-2.5 rounded-xl border border-manual-border bg-manual-soft/50 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim().length < 3) return;
            onChange({ reason: reason.trim(), until: until || null });
            setAdding(false);
            setReason("");
            setUntil("");
          }}
        >
          <label className="flex min-w-64 flex-1 flex-col gap-1 text-xs font-semibold text-ink-muted">
            Why {first} needs it
            <Input
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Covering night-shift dispatch"
              maxLength={300}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold text-ink-muted">
            Until (optional)
            <DateInput value={until} onChange={(iso) => setUntil(iso ?? "")} />
          </label>
          <Button type="submit" size="sm" disabled={reason.trim().length < 3}>
            <Plus className="h-4 w-4" /> Add for {first}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setAdding(false)}
          >
            Cancel
          </Button>
        </form>
      )}
    </li>
  );
}

// ------------------------------------------------------------------ C3: compare with role

function CompareWithRole({
  access: a,
  modules,
  labelOf,
  grants,
  readOnly,
  fromRole,
  onRemove,
}: RowProps & { onRemove: (code: string) => void }) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  const first = name(a.user.fullName);
  const roleNames = a.roles.map((r) => r.name).join(" and ") || "No role";
  const has = (c: string) =>
    fromRole(c).length > 0 || (grants.has(c) && !grants.get(c)!.ended);
  const total = a.permissions.length;
  const live = [...grants.values()].filter((g) => !g.ended).length;
  const rows = modules
    .map((m) => {
      const codes = m.controls.filter((c) => !c.startsWith("form:"));
      const any = codes.some(has);
      const lines = codes
        .filter((c) => !onlyDiff || has(c) !== fromRole(c).length > 0)
        .map((c) => ({ c, role: fromRole(c).length > 0, person: has(c) }));
      return {
        m,
        any,
        lines,
        diff: lines.filter((l) => l.role !== l.person).length,
      };
    })
    .filter((x) => x.any && x.lines.length);

  return (
    <div className="pb-24">
      <div className="mb-3 grid gap-3 md:grid-cols-2">
        <section className="flex items-center gap-3.5 rounded-2xl border border-border bg-surface-raised px-4 py-3.5 shadow-card">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-rail text-brand">
            <Layers className="h-4.5 w-4.5" aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-ink-muted">
              {a.roles.length > 1 ? "Roles" : "Role"}
            </div>
            <div className="truncate text-[15px] font-bold text-ink">
              {roleNames}
            </div>
            <div className="text-xs text-ink-muted">
              {a.roles
                .map((r) => `${plural(r.userCount, "person", "people")}`)
                .join(", ")}{" "}
              ·{" "}
              {plural(
                a.permissions.filter((p) => p.from.roles.length).length,
                "permission",
                "permissions"
              )}
            </div>
          </div>
          {a.roles[0] && (
            <Link
              to="/admin/roles"
              search={{ view: "role", role: a.roles[0].id }}
              className="text-sm font-bold text-brand-text hover:underline"
            >
              Open role
            </Link>
          )}
        </section>
        <section className="flex items-center gap-3.5 rounded-2xl border border-border bg-surface-raised px-4 py-3.5 shadow-card">
          <Avatar name={a.user.fullName} seed={a.user.email} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold text-ink-muted">Person</div>
            <div className="truncate text-[15px] font-bold text-ink">
              {a.user.fullName}
            </div>
            <div className="text-xs text-ink-muted">
              {a.user.facilities.map((f) => f.name).join(", ") || "No plant"} ·{" "}
              {plural(total, "permission", "permissions")} ·{" "}
              {plural(live, "personal grant", "personal grants")}
            </div>
          </div>
        </section>
      </div>
      <div className="mb-3 flex items-center gap-3">
        <p className="flex-1 text-sm text-ink-muted">
          Purple rows are where {first} differs from {roleNames}.
        </p>
        <ToggleGroup
          type="single"
          value={onlyDiff ? "diff" : "all"}
          onValueChange={(v) => v && setOnlyDiff(v === "diff")}
          aria-label="Rows shown"
        >
          <ToggleGroupItem value="diff">Only differences</ToggleGroupItem>
          <ToggleGroupItem value="all">All</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {rows.length ? (
        <section className="overflow-x-auto rounded-2xl border border-border bg-surface-raised shadow-card">
          {/* Each module's name sits above its own column headers (user, 2026-11-03). */}
          <table className="w-full min-w-[44rem] text-sm">
            {rows.map(({ m, lines, diff }) => {
              const Icon = MODULE_ICON[m.code] ?? SlidersHorizontal;
              return (
                <tbody key={m.code} className="border-t border-border">
                  <tr className="bg-surface-sunken">
                    <th colSpan={4} className="px-4 py-2.5 text-left">
                      <span className="flex items-center gap-2 font-extrabold text-ink">
                        <Icon className="h-4 w-4" aria-hidden />
                        {m.label}
                        <span
                          className={cn(
                            "ml-auto rounded-full border px-2 py-0.5 text-xs font-bold",
                            diff
                              ? "border-manual-border bg-manual-soft text-manual-text"
                              : "border-neutral-border bg-neutral-soft text-neutral-text"
                          )}
                        >
                          {diff ? `${diff} different` : "Same as role"}
                        </span>
                      </span>
                    </th>
                  </tr>
                  <tr className="border-t border-border-subtle text-left text-xs font-bold uppercase tracking-wide text-ink-muted">
                    <th scope="col" className="px-4 py-2.5">
                      Capability
                    </th>
                    <th scope="col" className="px-4 py-2.5">
                      Role gives
                    </th>
                    <th scope="col" className="px-4 py-2.5">
                      {first} has
                    </th>
                    <th scope="col" className="w-72 px-4 py-2.5">
                      Why different
                    </th>
                  </tr>
                  {lines.map((l) => {
                    const differs = l.role !== l.person;
                    return (
                      <tr
                        key={l.c}
                        className={cn(
                          "border-t border-border-subtle",
                          differs && "bg-manual-soft/70"
                        )}
                      >
                        <td className="px-4 py-2.5 font-semibold text-ink">
                          {labelOf.get(l.c) ?? l.c}
                        </td>
                        <td className="px-4 py-2.5">
                          <YesNo on={l.role} />
                        </td>
                        <td className="px-4 py-2.5">
                          <YesNo on={l.person} />
                        </td>
                        <td className="px-4 py-2.5">
                          {differs && (
                            <span className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-manual-text">
                                <User className="h-3.5 w-3.5" /> Personal grant
                              </span>
                              {!readOnly && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="ml-auto"
                                  onClick={() => onRemove(l.c)}
                                >
                                  <X className="h-3.5 w-3.5" /> Remove
                                </Button>
                              )}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              );
            })}
          </table>
        </section>
      ) : (
        <p className="rounded-2xl border border-dashed border-border-strong px-4 py-10 text-center text-sm text-ink-muted">
          {onlyDiff
            ? `${first} has exactly what the role gives.`
            : `${first} has no access yet.`}
        </p>
      )}
    </div>
  );
}

function YesNo({ on }: { on: boolean }) {
  return on ? (
    <span className="inline-flex items-center gap-1.5 font-bold text-success-text">
      <Check className="h-4 w-4" /> Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-ink-muted">
      <Minus className="h-4 w-4" /> No
    </span>
  );
}
